import { useEffect, useRef, useState } from 'react';

import { cohortsApi } from '../api';
import type { Cohort, PaginatedResponse } from '../types';

export interface UseMyCohortsResult {
  cohorts: Cohort[];
  loaded: boolean;
  /** Last request failed; `errorStatus` is null for statusless failures too. */
  failed: boolean;
  errorStatus: number | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

/**
 * Fetches the facilitator's own cohorts (`GET api/cohorts/v1/cohorts/`), newest
 * first, one page at a time. `loadMore` appends the next page; `hasMore` mirrors
 * whether the last response had a `next` link. `enabled: false` makes no request
 * at all and leaves `loaded` false.
 */
export function useMyCohorts(enabled: boolean = true): UseMyCohortsResult {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [nextPage, setNextPage] = useState<number | null>(1);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const hasFetchedData = useRef(false);

  const fetchPage = (page: number, append: boolean): void => {
    if (append) {
      setIsLoadingMore(true);
    }
    setFailed(false);
    setErrorStatus(null);

    cohortsApi.getMyCohorts(
      page,
      (res: PaginatedResponse<Cohort>) => {
        setCohorts((current) => (append ? [...current, ...res.results] : res.results));
        setNextPage(res.next ? page + 1 : null);
        setLoaded(true);
        setIsLoadingMore(false);
      },
      (error) => {
        setFailed(true);
        setErrorStatus((error as { status?: number })?.status ?? null);
        setLoaded(true);
        setIsLoadingMore(false);
      },
    );
  };

  useEffect(() => {
    if (!enabled || hasFetchedData.current) {
      return;
    }
    hasFetchedData.current = true;
    fetchPage(1, false);
  }, [enabled]);

  const loadMore = (): void => {
    if (nextPage) {
      fetchPage(nextPage, true);
    }
  };

  return {
    cohorts,
    loaded,
    failed,
    errorStatus,
    hasMore: nextPage !== null,
    isLoadingMore,
    loadMore,
  };
}
